import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, ListVideo, Tv, Building2, Package, Users, BarChart3,
  Phone, Mail, MessageCircle, User as UserIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { formatDate } from '@/utils/cn';

interface Tenant {
  id: number;
  razao_social: string;
  nome_fantasia?: string | null;
  subdomain: string;
  status: string;
  email_primary: string;
  email_billing?: string | null;
  phone?: string | null;
  contact_name?: string | null;
  current_plan?: { name?: string } | null;
  created_at?: string | null;
}

interface Props {
  tenant: Tenant;
  onClose: () => void;
}

// Normaliza telefone BR pro formato wa.me (apenas dígitos, com DDI 55).
function waLink(phone?: string | null): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  if (!digits) return null;
  if (!digits.startsWith('55')) digits = '55' + digits;
  return `https://wa.me/${digits}`;
}

function telLink(phone?: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits ? `tel:+${digits.startsWith('55') ? digits : '55' + digits}` : null;
}

// "há quanto tempo" simples em PT-BR a partir de uma data ISO.
function relativeTime(iso?: string | null): string {
  if (!iso) return 'nunca';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d}d`;
  return formatDate(iso);
}

function StatCard({ icon: Icon, label, value, hint, accent }: {
  icon: any; label: string; value: React.ReactNode; hint?: string; accent?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accent ?? ''}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

export function TenantDetailModal({ tenant, onClose }: Props) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const { data: s, isLoading } = useQuery({
    queryKey: ['admin-tenant-stats', tenant.id],
    queryFn: () => apiClient.admin.getTenantStats(tenant.id).then(r => r.data),
  });

  const wa = waLink(tenant.phone);
  const tel = telLink(tenant.phone);
  const noActivity = s && s.tvs_online === 0 && s.tvs_total === 0 && s.playlists === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="h-5 w-5 text-brand-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate">{tenant.nome_fantasia || tenant.razao_social}</h2>
              <div className="text-xs text-muted-foreground font-mono truncate">{tenant.subdomain}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Adoção / evolução */}
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Adoção do produto</div>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Carregando…</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatCard
                  icon={Tv} label="TVs conectadas"
                  value={<>{s?.tvs_online ?? 0}<span className="text-base text-muted-foreground font-normal"> / {s?.tvs_total ?? 0}</span></>}
                  hint={`${s?.tvs_paired ?? 0} pareadas · online = últimos 5 min`}
                  accent={(s?.tvs_online ?? 0) > 0 ? 'text-emerald-600' : 'text-muted-foreground'}
                />
                <StatCard
                  icon={ListVideo} label="Playlists"
                  value={s?.playlists ?? 0}
                  hint={s?.playlists_last7 ? `+${s.playlists_last7} nos últimos 7d` : 'sem novidades em 7d'}
                />
                <StatCard icon={Package} label="Produtos" value={s?.products ?? 0} />
                <StatCard icon={Building2} label="Lojas" value={s?.stores ?? 0} />
                <StatCard icon={Users} label="Usuários" value={s?.users ?? 0} />
                <StatCard
                  icon={BarChart3} label="Último acesso"
                  value={<span className="text-base">{relativeTime(s?.last_user_login_at)}</span>}
                  hint={`TV vista ${relativeTime(s?.last_tv_seen_at)}`}
                />
              </div>
            )}
            {noActivity && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                ⚠️ Cliente sem nenhuma atividade — sem TVs e sem playlists. Bom candidato a uma ligação de onboarding.
              </div>
            )}
            {!isLoading && !s?.onboarding_completed_at && (
              <div className="mt-2 text-[11px] text-muted-foreground">Onboarding ainda não concluído.</div>
            )}
          </div>

          {/* Contato — para o time acionar o cliente */}
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Falar com o cliente</div>
            <div className="rounded-lg border border-border divide-y divide-border">
              {tenant.contact_name && (
                <div className="flex items-center gap-2 px-3 py-2 text-sm">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{tenant.contact_name}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{tenant.phone || <span className="text-muted-foreground">Sem telefone cadastrado</span>}</span>
                </div>
                {tenant.phone && (
                  <div className="flex gap-1 shrink-0">
                    {wa && (
                      <a href={wa} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-600">
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    )}
                    {tel && (
                      <a href={tel}
                        className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-xs font-medium hover:bg-muted">
                        <Phone className="h-3.5 w-3.5" /> Ligar
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{tenant.email_primary}</span>
                </div>
                <a href={`mailto:${tenant.email_primary}`}
                  className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-xs font-medium hover:bg-muted shrink-0">
                  <Mail className="h-3.5 w-3.5" /> E-mail
                </a>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="muted">{tenant.current_plan?.name || 'sem plano'}</Badge>
              <span>Cliente desde {formatDate(tenant.created_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
